/* eslint-disable react/button-has-type */
/* eslint-disable indent */
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Button, Drawer, message, Select } from 'antd-v5';
import { format, set } from 'date-fns';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import ReportSelectDate from '../../../../CustomizedAntDesign/ReportSelectDate';
import ReportTagsFilters from '../../../../CustomizedAntDesign/ReportTagsFilters';
import { getRequest } from '../../../../../core/apiRequests';
import { getAppointmentsConfig, getAppointment } from '../../../../../services/appointments';
import ReportFilter from '../../../../CustomizedAntDesign/ReportFilter';
import { assignAssociateToAppointments, completeAppointments, getAppointments } from '../../../../../services/today';
import { AppointmentTable } from '../AppointmentTable';
import ConversationWindow from '../../../../../components/Layout/chat/chat';
import { getClientChat, resetMessaging } from '../../../../../reducers/messaging';
import { resetToday } from '../../../../../reducers/today';
import { AssignAssociatesModal } from '../../../../Layout/AssignAssociatesModal';
import AddEditAppointment from '../../../Clients/QuickView/Appointments/AddEditAppointment';
import { isEditable } from '../helpers/isEditable';

const AllAppointments = ({ auth }) => {
  const [filters, setFilters] = useState({
    statuses: [],
    appointmentTypeIds: [],
    associateIds: [],
    virtualFilter: [],
  });
  const [dateRange, setDateRange] = useState([
    dayjs.utc().startOf('day').toDate(),
    dayjs.utc().add(7, 'days').endOf('day').toDate(),
  ]);
  const stores = useMemo(() => auth?.stores?.filter(store => store.id !== 0) || [], [auth?.stores]);
  const [storeId, setStoreId] = useState(auth.selectedStore || stores[0]?.id || null);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const handleSelectedRowKeysChange = useCallback(selected => {
    setSelectedRowKeys(selected);
  }, []);

  const handleFilterChange = useCallback(value => {
    const updatedFilters = {
      statuses: [],
      appointmentTypeIds: [],
      storeIds: [],
      associateIds: [],
      virtualFilter: [],
    };
    value.forEach(item => {
      if (item[0] && item[1]) {
        updatedFilters[item[0]] = [...updatedFilters[item[0]], item[1]];
      } else {
        updatedFilters[item[0]] = [];
      }
    });
    setFilters(updatedFilters);
  }, []);

  const removeFilter = useCallback((key, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [key]: prevFilters[key].filter(item => item !== value),
    }));
  }, []);

  const handleOnDateRangeChange = useCallback(dates => {
    if (dates.length === 2) {
      const utcStart = new Date(dates[0].getUTCFullYear(), dates[0].getUTCMonth(), dates[0].getUTCDate());
      const utcEnd = new Date(dates[1].getUTCFullYear(), dates[1].getUTCMonth(), dates[1].getUTCDate());
      setDateRange([utcStart, utcEnd]);
      setPage(1);
    }
  }, []);

  const { data: associates } = useQuery(
    ['associates-list'],
    async () => {
      const reqObj = {
        params: ['associates'],
        loadingStatusOff: true,
      };
      const response = await getRequest(reqObj);
      return response;
    },
    {
      enabled: !!auth.merchantId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const { data: appointmentsConfig } = useQuery(
    ['appointments-config'],
    async () => {
      return getAppointmentsConfig();
    },
    {
      refetchOnWindowFocus: false,
      initialData: {},
      enabled: !!auth.merchantId,
    },
  );

  const filterOptions = useMemo(() => {
    const status = appointmentsConfig?.statuses
      ? [
        {
          title: 'Status',
          key: '0-1',
          value: 'statuses',
          children: appointmentsConfig.statuses.map(status => ({
            title: status.name,
            value: status.code ? `${status.code}` : '',
            key: `0-1-${status.id}`,
          })),
        },
      ]
      : [];

    const types = appointmentsConfig?.appointmentTypes
      ? [
        {
          title: 'Type',
          key: '0-2',
          value: 'appointmentTypeIds',
          children: appointmentsConfig.appointmentTypes.map(type => ({
            title: type.name,
            value: type.id ? `${type.id}` : '',
            key: `0-2-${type.id}`,
          })),
        },
      ]
      : [];
    const associatesFilters =
      auth.role !== 'ASSOCIATE'
        ? [
          {
            title: 'Associate',
            key: '0-4',
            value: 'associateIds',
            children: associates.map(associate => ({
              title: `${associate?.first_name ? `${associate.first_name} ` : ''}${associate?.last_name || ''}`,
              value: associate.id ? `${associate.id}` : '',
              key: `0-4-${associate.id}`,
            })),
          },
        ]
        : [];

    const virtualFilters = [
      {
        title: 'In-Person / Virtual',
        key: '0-5',
        value: 'virtualFilter',
        children: [
          {
            title: 'In-Person',
            value: 'in-person',
            key: '0-5-1',
          },
          {
            title: 'Virtual',
            value: 'virtual',
            key: '0-5-2',
          },
        ],
      },
    ];

    return [...status, ...types, ...associatesFilters, ...virtualFilters];
  }, [auth.stores, associates, appointmentsConfig]);

  const {
    data: appointments,
    isFetching,
    refetch,
  } = useQuery(
    ['todayAppointments', dateRange, filters, page, pageSize, storeId],
    async () => {
      try {
        const query = Object.entries(filters).reduce((acc, [key, value]) => {
          // Skip virtualFilter as it needs special handling
          if (key === 'virtualFilter') {
            return acc;
          }
          if (value.length > 0) {
            acc[key] = value;
          }
          return acc;
        }, {});

        // Handle virtual filter - pass to API
        if (filters.virtualFilter.length > 0) {
          // If both are selected, don't filter (show all)
          // If only one is selected, filter by that
          if (filters.virtualFilter.length === 1) {
            query.virtual = filters.virtualFilter[0] === 'virtual';
          }
        }

        if (auth.role === 'ASSOCIATE') {
          query.associateIds = [auth.userId];
        }
        query.storeIds = storeId ? [storeId] : [];

        const finalQuery = {
          limit: pageSize,
          offset: (page - 1) * pageSize,
          sort: 'appointmentDateTime',
          ...query,
          fromDate: format(dateRange[0], 'yyyy-MM-dd'),
          toDate: format(dateRange[1], 'yyyy-MM-dd'),
        };

        console.log('🚀 API Query being sent:', finalQuery);

        return getAppointments({
          query: finalQuery,
          loadingStatusOff: true,
        });
      } catch (error) {
        console.error('Error fetching appointments:', error);
        return {
          data: [],
          total: 0,
          page: 1,
          pageSize: 10,
        };
      }
    },
    {
      refetchOnWindowFocus: false,
      initialData: {
        data: [],
        total: 0,
        page: 1,
        pageSize: 10,
      },
      enabled: !!auth?.merchantId,
    },
  );

  // Filter appointments on the frontend based on virtual filter
  const filteredAppointments = useMemo(() => {
    if (!appointments?.data) return [];

    // If no virtual filter is selected, or both are selected, show all
    if (filters.virtualFilter.length === 0 || filters.virtualFilter.length === 2) {
      return appointments.data;
    }

    // Filter based on the selected option
    const showVirtual = filters.virtualFilter.includes('virtual');
    const showInPerson = filters.virtualFilter.includes('in-person');

    return appointments.data.filter(appointment => {
      if (showVirtual && appointment.virtual) return true;
      if (showInPerson && !appointment.virtual) return true;
      return false;
    });
  }, [appointments?.data, filters.virtualFilter]);

  const handleComplete = useCallback(async () => {
    try {
      await completeAppointments(selectedRowKeys);
      refetch();
      message.success('Appointments completed successfully');
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('Error completing appointments:', error);
      message.error('Error completing appointments');
    }
  }, [selectedRowKeys, refetch]);

  const handleAssign = useCallback(
    async associateId => {
      try {
        await assignAssociateToAppointments(selectedRowKeys, associateId, true);
        refetch();
        message.success('Appointments assigned successfully');
        setOpenAssignModal(false);
        setSelectedRowKeys([]);
        setSelectedAppointment(null);
      } catch (error) {
        console.error('Error assigning appointments:', error);
        message.error('Error assigning appointments');
      }
    },
    [selectedRowKeys, refetch],
  );

  const handleEdit = useCallback(async appointment => {
    try {
      const response = await getAppointment(appointment.id);
      const fullAppointment = response?.data || response;
      setSelectedAppointment(fullAppointment);
    } catch (error) {
      console.error('Failed to fetch appointment details:', error);
      setSelectedAppointment(appointment);
    }
    setOpenEditModal(true);
  }, []);

  const closeChat = () => {
    setShowChat(false);
    resetMessaging();
    resetToday();
  };

  const setSelectedClient = async cli => {
    await getClientChat(cli);
    setShowChat(true);
  };

  const handleCloseModal = useCallback(() => {
    setOpenEditModal(false);
    setSelectedAppointment(null);
  }, []);

  const selectedStoreRow = useMemo(() => {
    const selectedAppointments = appointments?.data?.filter(appointment => selectedRowKeys?.includes(appointment.id));
    return (
      selectedAppointments?.map(app => app?.store?.id).filter((id, index, self) => self.indexOf(id) === index) || []
    );
  }, [selectedRowKeys, appointments?.data]);

  return (
    <StandardLayoutV2
      title="Appointments"
      breadcrumb={[
        {
          path: '/',
          title: 'Today',
        },
        {
          title: 'Appointments',
        },
      ]}
      classProps="flex-col-left "
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
    >
      <div className="flex-row-spacebetween-nowrap w-100-P">
        <div className="flex-row-center" style={{ gap: '16px' }}>
          <ReportSelectDate
            onChange={handleOnDateRangeChange}
            defaultValue="next7days"
            type="custom-options"
            customOptions={[
              { label: 'Today', value: 'today' },
              { label: 'Next 3 days', value: 'next3days' },
              { label: 'Next 7 days', value: 'next7days' },
              { label: 'This Week', value: 'thisweek' },
              { label: 'This Month', value: 'this-month' },
              { label: 'This Year', value: 'thisyear' },
              { label: 'Custom date range', value: 'custom' },
            ]}
          />
          {stores.length > 1 && (
            <Select
              options={stores.filter(store => store.id !== 0).map(store => ({ label: store.name, value: store.id }))}
              placeholder="Select Store"
              defaultValue={auth.selectedStore || stores[0]?.id || null}
              value={storeId}
              onChange={value => setStoreId(value)}
              popupMatchSelectWidth={false}
            />
          )}
          <ReportFilter options={filterOptions} handleFilterChange={handleFilterChange} filters={filters} />
        </div>
        {selectedRowKeys.length > 0 && (
          <div className="cb-link-btn-group cb-link-btn-group--wide">
            <Button type="link" className="cb-link-btn-action" onClick={() => setOpenAssignModal(true)}>
              <FontAwesomeIcon icon={['far', 'user']} />
              Assign Associate
            </Button>
            <Button type="link" className="cb-link-btn-action cb-link-btn-action--icon-lg" onClick={handleComplete}>
              <FontAwesomeIcon icon={['far', 'check-circle']} />
              Complete
            </Button>
          </div>
        )}
      </div>
      <ReportTagsFilters options={filterOptions} filterSelected={filters} removeFilter={removeFilter} />
      <AppointmentTable
        data={appointments?.data || []}
        appointmentsConfig={appointmentsConfig}
        formatDate="h:mmaaa MMM dd, yyyy"
        handleEdit={handleEdit}
        handleMessage={client => setSelectedClient(client)}
        tableProps={{
          pagination: {
            pageSize,
            current: page,
            total: appointments.total,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
            showSizeChanger: { showSearch: false },
          },
          rowKey: item => item.id,
          rowSelection: {
            selectedRowKeys,
            onChange: handleSelectedRowKeysChange,
            getCheckboxProps: record => ({ disabled: !isEditable(record.status) }),
          },
          loading: isFetching,
        }}
      />
      <AddEditAppointment
        isOpen={openEditModal}
        onClose={handleCloseModal}
        appointment={selectedAppointment}
        onSuccess={refetch}
        allAssoc={associates}
        eventId={selectedAppointment?.event?.id}
        eventName={selectedAppointment?.event?.name}
        eventStartDate={selectedAppointment?.event?.startDate}
        eventEndDate={selectedAppointment?.event?.endDate}
        disableEventSelect={!!selectedAppointment?.event?.id}
      />
      <AssignAssociatesModal
        isOpen={openAssignModal}
        onClose={() => setOpenAssignModal(false)}
        onAssign={handleAssign}
        allAssoc={associates.filter(assoc => selectedStoreRow.every(storeId => assoc.store_ids.includes(storeId)))}
        warning={
          selectedStoreRow.length > 1
            ? "You've selected appointments from multiple stores. Options are limited to associates assigned to ALL selected stores."
            : null
        }
      />
      <Drawer
        styles={{
          body: {
            padding: 0,
            overflow: 'hidden',
          },
        }}
        closable={false}
        push={false}
        destroyOnClose
        width={750}
        open={showChat}
        onClose={closeChat}
      >
        <div style={{ height: '100vh' }}>
          <ConversationWindow setDrawerToggle={closeChat} chatIconColor="#EBEBEB" type="right" />
        </div>
      </Drawer>
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllAppointments);
